import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { questionBanks, testSchemas } from '../api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

function TestSchemas() {
  const [schemas, setSchemas] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const navigate = useNavigate();
  const showToast = useToast();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const highlightSchemaId = searchParams.get('highlight');

  useEffect(() => {
    loadData();
    // Убираем параметр highlight через 5 секунд
    if (highlightSchemaId) {
      const timer = setTimeout(() => {
        navigate('/test-schemas', { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightSchemaId, navigate]);

  const loadData = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [schemasResponse, banksResponse] = await Promise.all([
        testSchemas.getAll(),
        questionBanks.getAll(),
      ]);
      setSchemas(schemasResponse.data);
      // Сортируем базы от новых к старым (по id или uploaded_at)
      const sortedBanks = [...(banksResponse.data || [])].sort((a, b) => {
        // Сначала пробуем по uploaded_at, если есть
        if (a.uploaded_at && b.uploaded_at) {
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
        }
        // Иначе по id (больший id = новее)
        return b.id - a.id;
      });
      setBanks(sortedBanks);
    } catch (err) {
      console.error('Failed to load data', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Тест-схемы</h1>
      </div>

      {loading && (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
      )}

      {!loading && loadError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4"><span>Не удалось загрузить данные</span><button onClick={loadData} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 flex-shrink-0">Повторить</button></div>
      )}

      {!loading && !loadError && banks.length === 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-xl shadow-sm mb-8">
          <h3 className="text-yellow-800 mb-3 font-semibold">База вопросов отсутствует</h3>
          <p className="text-yellow-700 mb-4">
            Для создания тест-схемы необходимо сначала загрузить хотя бы одну базу вопросов.
          </p>
          <button 
            onClick={() => navigate('/question-banks')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5"
          >
            Перейти к загрузке базы вопросов
          </button>
        </div>
      )}

      <div className="mt-8">
        {schemas.length === 0 && banks.length > 0 ? (
          <div className="space-y-8">
            {banks.map((bank) => (
              <div key={bank.id}>
                {/* Заголовок базы */}
                <div className="bg-gray-100 px-6 py-4 rounded-t-xl border-b-2 border-gray-300 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold m-0 text-gray-800">
                      {bank.name}
                    </h2>
                    <p className="text-sm mt-1 m-0 text-gray-600">
                      {bank.questions_count || 0} вопросов в базе
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/test-schemas/create/${bank.id}`)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                  >
                    + Создать тест-схему
                  </button>
                </div>

                {/* Пустая таблица */}
                <div className="bg-white rounded-b-xl border border-t-0 border-gray-200 p-8 text-center shadow-sm">
                  <p className="text-gray-500 m-0">
                    Тест-схем пока нет
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : banks.length > 0 ? (
          <div className="space-y-8">
            {banks.map((bank) => {
              const bankSchemas = schemas.filter(s => s.question_bank_id === bank.id);
              
              return (
                <div key={bank.id}>
                  {/* Заголовок базы */}
                  <div className="bg-gray-100 px-6 py-4 rounded-t-xl border-b-2 border-gray-300 flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold m-0 text-gray-800">
                        {bank.name}
                      </h2>
                      <p className="text-sm mt-1 m-0 text-gray-600">
                        {bank.questions_count || 0} вопросов в базе • {bankSchemas.length} {bankSchemas.length === 1 ? 'схема' : bankSchemas.length < 5 ? 'схемы' : 'схем'}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/test-schemas/create/${bank.id}`)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                    >
                      + Создать тест-схему
                    </button>
                  </div>
                  
                  {/* Таблица схем - Desktop */}
                  <div className="hidden md:block bg-white rounded-b-xl border border-t-0 border-gray-200 overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b-2 border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Название схемы</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-32">Вопросов</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-32">Приглашений</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-36">Создано</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 w-72">Действия</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bankSchemas.map((schema, idx) => {
                            const isHighlighted = highlightSchemaId && parseInt(highlightSchemaId) === schema.id;
                            return (
                            <tr 
                              key={schema.id}
                              className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${isHighlighted ? 'bg-blue-50' : ''}`}
                            >
                              <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                {schema.name}
                              </td>
                              <td className="px-4 py-4 text-center text-sm text-gray-600">
                                <strong>{schema.num_questions}</strong>
                              </td>
                              <td className="px-4 py-4 text-center text-sm text-gray-600">
                                <strong>{schema.invitations_count || 0}</strong>
                              </td>
                              <td className="px-4 py-4 text-center text-xs text-gray-500">
                                {new Date(schema.created_at).toLocaleDateString('ru-RU')}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex flex-col items-end gap-1">
                                  {isHighlighted && (
                                    <span className="text-xs text-blue-600 font-medium">Следующий шаг: создать приглашения</span>
                                  )}
                                  <div className="flex gap-2 justify-end">
                                    <button
                                      onClick={() => navigate(`/test-schemas/${schema.id}/invitations`)}
                                      className={`px-4 py-2 rounded-lg text-xs font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5 relative group ${
                                        isHighlighted 
                                          ? 'bg-blue-600 text-white ring-2 ring-blue-300' 
                                          : 'bg-blue-600 text-white'
                                      }`}
                                    >
                                      <span className="relative z-10">Приглашения</span>
                                      <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-400 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></span>
                                    </button>
                                  <button
                                    onClick={() => {
                                      const isProduction = window.location.hostname === 'skillgate.pro' || window.location.hostname === 'www.skillgate.pro';
                                      const baseUrl = isProduction 
                                        ? 'https://skillgate.pro'
                                        : `http://${window.location.hostname}:8000`;
                                      window.open(`${baseUrl}/candidate?demo=${schema.id}`, '_blank');
                                    }}
                                    className="bg-green-500 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-green-600 transition-colors"
                                    title="Открыть демо-тестирование"
                                  >
                                    Демо
                                  </button>
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();

                                      if (schema.invitations_count > 0) {
                                        showToast(`Невозможно удалить схему: существует ${schema.invitations_count} приглашений. Сначала архивируйте или удалите все приглашения.`, 'error');
                                        return;
                                      }

                                      if (!(await confirm({
                                        title: `Удалить тест-схему «${schema.name}»?`,
                                        message: 'Это действие нельзя отменить.',
                                        confirmText: 'Удалить',
                                        danger: true,
                                      }))) return;

                                      try {
                                        await testSchemas.delete(schema.id);
                                        showToast(`Тест-схема «${schema.name}» удалена`);
                                        loadData();
                                      } catch (err) {
                                        showToast('Ошибка удаления: ' + (err.response?.data?.detail || err.message), 'error');
                                      }
                                    }}
                                    className={`w-9 h-9 flex items-center justify-center text-sm rounded-lg transition-all ${
                                      schema.invitations_count > 0 
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-60' 
                                        : 'bg-red-500 text-white hover:bg-red-600 hover:scale-105'
                                    }`}
                                    title={schema.invitations_count > 0 ? 'Сначала удалите все приглашения' : 'Удалить тест-схему'}
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Карточки схем - Mobile */}
                  <div className="md:hidden bg-white rounded-b-xl border border-t-0 border-gray-200 shadow-sm">
                    <div className="divide-y divide-gray-200">
                      {bankSchemas.map((schema) => {
                        const isHighlighted = highlightSchemaId && parseInt(highlightSchemaId) === schema.id;
                        return (
                        <div key={schema.id} className={`p-4 ${isHighlighted ? 'bg-blue-50' : ''}`}>
                          <div className="mb-3">
                            <h3 className="text-sm font-semibold text-gray-900 mb-2">{schema.name}</h3>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                              <span>{schema.num_questions} вопросов</span>
                              <span>{schema.invitations_count || 0} приглашений</span>
                              <span>{new Date(schema.created_at).toLocaleDateString('ru-RU')}</span>
                            </div>
                            {isHighlighted && (
                              <p className="text-xs text-blue-600 font-medium mt-2">Следующий шаг: создать приглашения</p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => navigate(`/test-schemas/${schema.id}/invitations`)}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                isHighlighted
                                  ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                                  : 'bg-blue-600 text-white'
                              }`}
                            >
                              Приглашения
                            </button>
                            <button
                              onClick={() => {
                                const isProduction = window.location.hostname === 'skillgate.pro' || window.location.hostname === 'www.skillgate.pro';
                                const baseUrl = isProduction 
                                  ? 'https://skillgate.pro'
                                  : `http://${window.location.hostname}:8000`;
                                window.open(`${baseUrl}/candidate?demo=${schema.id}`, '_blank');
                              }}
                              className="bg-green-500 text-white px-3 py-2 rounded-lg text-xs font-semibold"
                            >
                              Демо
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();

                                if (schema.invitations_count > 0) {
                                  showToast(`Невозможно удалить схему: существует ${schema.invitations_count} приглашений. Сначала архивируйте или удалите все приглашения.`, 'error');
                                  return;
                                }

                                if (!(await confirm({
                                  title: `Удалить тест-схему «${schema.name}»?`,
                                  message: 'Это действие нельзя отменить.',
                                  confirmText: 'Удалить',
                                  danger: true,
                                }))) return;

                                try {
                                  await testSchemas.delete(schema.id);
                                  showToast(`Тест-схема «${schema.name}» удалена`);
                                  loadData();
                                } catch (err) {
                                  showToast('Ошибка удаления: ' + (err.response?.data?.detail || err.message), 'error');
                                }
                              }}
                              className={`w-10 h-10 flex items-center justify-center text-sm rounded-lg ${
                                schema.invitations_count > 0 
                                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-60' 
                                  : 'bg-red-500 text-white'
                              }`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </>
  );
}

export default TestSchemas;
